import { FormikContextType } from "formik";
import React, { useEffect, useState, useRef } from "react";
import { Socket } from "socket.io-client";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import FeedbackModal from "../../provider/components/FeedbackModal";
import { ReactComponent as EmojiIcon } from "../../svgs/emoji-icon.svg";
import { ReactComponent as SendChatIcon } from "../../svgs/send-chat.svg";
import { UserType } from "../../types/common";
import { generateUUID } from "../../utils/helpers";
import Spacer from "../Spacer";
import Button from "../shared/Button";

type MessageInputFieldProps = {
  onChange?: (e: React.ChangeEvent<any>) => void;
  onBlur?: (e: React.FocusEvent<any, Element>) => void;
  value: string;
  placeholder: string;
  name: string;
  size?: string;
  disabled?: boolean;
  width?: number;
  onSend: () => void;
  onAttach: () => void;
  currentUserId: string | number;
  receiverId: string | number;
  socket: Socket;
  formik: FormikContextType<{ message: string }>;
  userType: UserType;
}

const MessageInputField: React.FC<MessageInputFieldProps> = ({
  onChange = () => {},
  onBlur = () => {},
  value,
  placeholder,
  size,
  name,
  disabled = false,
  width,
  onSend,
  onAttach,
  currentUserId,
  receiverId,
  socket,
  formik,
  userType
}) => {
  const [error, setError] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  const handleSubmit = async (e: any) => {
    if (!formik.isSubmitting) {
      e.preventDefault();

      if (message.trim().length) {
        const to =
          userType === "patient"
            ? `${currentUserId}_${receiverId}`
            : `${receiverId}_${currentUserId}`;
        console.log(message)
        socket.emit("SEND_MESSAGE", {
          message: message,
          senderId: currentUserId,
          receiverId,
          userType,
          messageId: generateUUID(),
          isAudio: false,
          isImage: false,
          isVideo: false,
          to
        });
        onSend();
        formik.resetForm();
        setMessage(''); // Clear local state
      }
    }
  };

  const computedRootStyle = () => {
    const style: { [key: string]: number | string } = {};

    if (width) {
      style.width = `${width}px`;
    }

    return style;
  };
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const MIN_TEXTAREA_HEIGHT = 20; 
  const MAX_TEXTAREA_HEIGHT = 200; 

  useEffect(() => {
    if (!textareaRef.current) return;
    
    textareaRef.current.style.height = 'auto';
    
    const newHeight = Math.min(
      Math.max(textareaRef.current.scrollHeight, MIN_TEXTAREA_HEIGHT),
      MAX_TEXTAREA_HEIGHT
    );
    
    textareaRef.current.style.height = `${newHeight}px`;
  }, [value]);

  const [message, setMessage] = useState(value);

  // Sync local message state with formik value
  useEffect(() => {
    setMessage(value);
  }, [value]);

  const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setMessage(newValue);
    // Update formik value
    formik.setFieldValue(name, newValue);
    // Call the original onChange if provided
    if (onChange) {
      onChange(e);
    }
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const cursorPosition = textareaRef.current?.selectionStart || 0;
    const textBeforeCursor = message.substring(0, cursorPosition);
    const textAfterCursor = message.substring(cursorPosition);
    const newValue = textBeforeCursor + emojiData.emoji + textAfterCursor;
    
    // Update local state
    setMessage(newValue);
    // Update formik value
    formik.setFieldValue(name, newValue);
    
    // Move cursor after the inserted emoji
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPosition = cursorPosition + emojiData.emoji.length;
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="w-full flex flex-col" style={computedRootStyle()}>
      <div className="w-full lg:max-w-full lg:w-[calc(80%-348px)] flex items-center justify-between border-t border-gray-200 py-4 mx-0 leading-tight focus:outline-none focus:bg-white bg-white focus:border-gray-500 fixed bottom-0 z-9999">
        <textarea
          ref={textareaRef}
          className="appearance-none resize-none text-[#103C1B] text-[16px] border border-none focus:outline-none lg:text-xs w-full px-2 overflow-y-auto"
          style={{
            minHeight: `${MIN_TEXTAREA_HEIGHT}px`,
            maxHeight: `${MAX_TEXTAREA_HEIGHT}px`,
          }}
          name={name}
          rows={1}
          placeholder={placeholder}
          value={message}
          onChange={handleOnChange}
          onBlur={onBlur}
          disabled={disabled}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey && message.trim()) {
              event.preventDefault();
              handleSubmit(event);
            }
          }}
        />
        <div className="w-fit flex items-center justify-end mr-4 lg:mr-0">
          {/* <button onClick={onAttach}>
            <AttachIcon />
          </button>
          <Spacer width={12} /> */}
          <div className="relative" ref={emojiPickerRef}>
            <button 
              type="button" 
              onClick={(e) => {
                e.preventDefault();
                setShowEmojiPicker(!showEmojiPicker);
              }}
              className="focus:outline-none"
            >
              <EmojiIcon />
            </button>
            {showEmojiPicker && (
              <div className="absolute bottom-10 right-0 z-50">
                <EmojiPicker 
                  onEmojiClick={handleEmojiClick}
                  width={300}
                  height={350}
                  skinTonesDisabled
                  searchDisabled={false}
                  previewConfig={{
                    showPreview: false
                  }}
                />
              </div>
            )}
          </div>
          <Spacer width={12} />

          <Button
            type="submit"
            variant="primary"
            label="Send"
            icon={<SendChatIcon />}
            iconPosition="right"
            size="small"
            onClick={e => {
              handleSubmit(e);
            }}
          />
        </div>
      </div>
      <FeedbackModal
        closeModal={() => setError(false)}
        isVisible={error}
        message="This patient does not have an account with TempleHS, but the message will be delivered once the patient registers an account"
        title="Patient Not Registered"
      />
    </div>
  );
};

export default MessageInputField;
